<template>
<div>
  <div>
    <h3 v-if="project">{{ project.name }}</h3>
    <div class="form-error p-2 border-radius" v-if="!hasEditPermission">
      {{ $t('merge.noEditPermission') }}
    </div>
    {{ $t('merge.projectVersion') }}: {{ projectVersion }}
    <div class="form-error p-2" v-if="!versionIsValid">
      <p>{{ $t('merge.versionInvalidSolution', { version: minimalProjectVersion }) }}</p>
    </div>
    <!--Project instances-->
    <h4 class="mt-2">Instances</h4>
    <loading-spinner v-if="loading" />
    <table v-if="project">
      <tr>
        <th>{{ $t('merge.instanceName') }}</th>
        <th>{{ $t('merge.instanceType') }}</th>
      </tr>
      <tr
          :class="{'errored': instance.error}"
          v-for="instance in project.instances"
          :key="instance.id">
        <td v-if="instance.error">{{ instance.error == "401" ? $t('errors.accessDenied') : $t('errors.objectNotFound') }}</td>
        <td v-else>{{ instance.name }}
          <span v-if="$store.getters.isSuperAdmin"><br />instanceId:<br />{{instance.id}}<br />assetId:<br />{{ instance.assetId }} )</span></td>
        <td>{{ instance.type }}</td>
      </tr>
      <tr v-if="!projectInstances.length">
        <td colspan="2">{{ $t('merge.noInstances') }}</td>
      </tr>
    </table>
    <div v-if="showButton" @click="openLinkInNewWindow" class="settings-button d-inline-block mt-2">{{ $t('merge.GoToProject') }}</div>
  </div>
</div>
</template>

<script>
import {compareVersion} from "@/utils";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
export default {
  name: "ProjectMerge",
  components: {
    LoadingSpinner
  },
  props: {
    projectId: {
      type: String,
      required: true
    },
    showButton: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      project: null,
      projectVersion: 'unknown',
      minimalProjectVersion: '3.1.3',
      versionIsValid: true,
      hasEditPermission: true,
      loading: false
    };
  },
  computed: {
    projectInstances() {
      return this.project ? this.project.instances : [];
    },
  },
  watch: {
    projectInstances: {
      immediate: true,
      handler(newVal) {
        const erroredInstances = newVal.filter(instance => instance.error);
        if (erroredInstances.length > 0) {
          this.$emit('instancesErrored');
        } else {
          this.$emit('instancesFine');
        }
      }
    },
    projectId: {
      immediate: true,
      handler(newVal) {
        this.loadProject(newVal);
      }
    },
    hasEditPermission: {
      immediate: true,
      handler(newVal) {
        this.$emit('permission', newVal);
      }
    }
  },
  mounted() {
    this.loadProject();
  },
  methods: {
    async checkPermission(project) {
      const {organizationId, teams} = project;
      this.hasEditPermission = await this.$store.dispatch('checkTeamOrgPermission', {
        op: 'write',
        organizationId,
        teams
      });
    },
    openLinkInNewWindow() {
      if (!this.project || !this.project.id) {
        console.warn("Project not loaded or ID is missing.");
        return;
      }
      const routeData = this.$router.resolve({
        name: 'project_general',
        params: { id: this.project.id }
      });
      window.open(routeData.href, '_blank');
    },
    setVersion() {
      if (this.project && this.project.metaValues) {
        const versionMeta = this.project.metaValues.find(meta => meta.metaFieldId === '5c9a2c74-5a3b-43bb-8bac-60c14e5325e3');
        this.projectVersion = versionMeta ? versionMeta.value : 'unknown';
      } else {
        this.projectVersion = 'unknown';
      }
      this.$emit('versionSet', this.projectVersion);
      this.versionIsValid = this.projectVersion && this.projectVersion !== 'unknown' && compareVersion(this.projectVersion, this.minimalProjectVersion) >= 0;
    },
    async loadInstanceAssets(projectInstances) {
      if(projectInstances) {
        for(let i = 0; i < projectInstances.length; i++) {
          const instance = projectInstances[i];
          if (instance.assetId) {
            try {
              const asset = await this.$store.dispatch('clientLoadAsset', { id: instance.assetId });
              console.log(`Loaded asset for instance ${instance.id}:`, asset);
              projectInstances[i].asset = asset;
            } catch (error) {
              projectInstances[i].error = error.statusCode;
              console.error(`Error loading asset for instance ${instance.id}:`, error);
            }
          }
        }
      }
      return projectInstances;
    },
    async loadProject() {
      if(!this.projectId) {
        console.warn("No project ID provided.");
        return null;
      }
      try {
        this.loading = true;
        // Simulate an API call to fetch project details
        const project = await this.$store.dispatch('clientLoadProject', {
          id: this.projectId,
          include: ['instances', 'teams', 'tags', 'metaValues']
        });
        let instances = [];
        console.log('Loaded project:', project);
        if(project.instances && project.instances.length > 0) {
          instances = await this.loadInstanceAssets(project.instances);
        }
        this.project = {
          ...project,
          instances: instances || []
        };
        this.setVersion();
        await this.checkPermission(project);
        this.loading = false;
      } catch (error) {
        console.error("Error loading project:", error);
        this.loading = false;
        return null;
      }
    },
  }
}
</script>

<style scoped lang="scss">
table {
  th {
    background-color: var(--vform-editor-ui-secondary-color);
  }
  td {
    padding: 0.5rem;
    border: 1px solid var(--vform-editor-ui-secondary-color);
    background-color: var(--vform-editor-ui-tertiary-color);
    color: #444;
  }
  tr {
    &.errored {
      td {
        background-color: $highlight;
        color: #fff;
      }
    }
  }
  /*tr:nth-child(even) {
    background-color: var(--vform-editor-ui-tertiary-color);
    color: #444;
  }*/
}
</style>